import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setQuery } from "../../store/slices/holidaysSlice.ts";
import { notification } from "../../utils/Notifications.tsx";
import { readBranches } from "../../services/branch.service.ts";

const initialValues = {
  status: "all",
  param: "",
  holidayStartDate: "",
  holidayEndDate: "",
  branchId: "all",
};

interface FilterHolidaysProps {
  children?: React.ReactNode;
}

const FilterHolidays = ({ children }: FilterHolidaysProps) => {
  const dispatch = useDispatch();
  const [filters, setFilters] = useState(initialValues);
  const [timer, setTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [branches, setBranches] = useState<any[]>([]);
  const [loadingBranches, setLoadingBranches] = useState(false);

  const buildQuery = (source: typeof filters) =>
    Object.keys(source)
      .filter(
        (key) =>
          (source as any)[key] !== -1 &&
          (source as any)[key] !== "" &&
          (source as any)[key] !== "all"
      )
      .map(
        (key) =>
          `${encodeURIComponent(key)}=${encodeURIComponent((source as any)[key])}`
      )
      .join("&");

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setFilters((prev) => ({ ...prev, param: newValue }));
    if (timer) clearTimeout(timer);
    setTimer(
      setTimeout(() => {
        const updated = { ...filters, param: newValue };
        const queryString = buildQuery(updated);
        dispatch(setQuery(queryString));
        setTimer(null);
      }, 500)
    );
  };

  const updateFilters = (params?: Partial<typeof filters>) => {
    const filtros = { ...filters, ...params };
    const queryString = buildQuery(filtros);
    dispatch(setQuery(queryString));
  };

  const selectedFiltersList = () => {
    const selected: Array<{ key: string; value: string; valueName: string; keyName: string }> = [];
    for (const key of Object.keys(filters)) {
      const value = (filters as any)[key];
      if (value === "all" || value === "") continue;

      let valueName = value;
      let keyName = key;

      switch (key) {
        case "param":
          keyName = "Búsqueda";
          break;
        case "status":
          keyName = "Estado";
          valueName =
            value === "pending"
              ? "Pendientes"
              : value === "approved"
              ? "Aprobados"
              : value === "rejected"
              ? "Rechazados"
              : value;
          break;
        case "branchId":
          keyName = "Sucursal";
          valueName = branches.find((b) => b.id === parseInt(value, 10))?.name ?? value;
          break;
        default:
          break;
      }
      selected.push({ key, value, valueName, keyName });
    }
    return selected;
  };

  const removeFilter = (key: string) => {
    const updated = { ...filters, [key]: (initialValues as any)[key] };
    setFilters(updated);
    dispatch(setQuery(buildQuery(updated)));
  };

  const fetchBranches = async () => {
    try {
      setLoadingBranches(true);
      const resp = await readBranches();
      setBranches(resp || []);
    } catch (error) {
      console.error("Error fetching branches:", error);
      notification("Error", "Error al cargar sucursales", "danger");
    } finally {
      setLoadingBranches(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  const selected = selectedFiltersList();

  return (
    <div className="w-100">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          flexWrap: "nowrap",
          marginBottom: "0.5rem",
        }}
      >
        <div className="input-group mb-0" style={{ flex: "1 1 0", minWidth: 0 }}>
          <button
            className="btn btn-white dropdown-toggle"
            type="button"
            data-bs-toggle="dropdown"
            style={{ borderRadius: "999px 0 0 999px" }}
          >
            <span className="d-none d-md-inline">Filtrar </span>
            <span className="d-inline d-md-none">
              <i className="fa fa-filter" />
            </span>
            <b className="caret" />
          </button>
          <div
            className="dropdown-menu p-4"
            onClick={(e) => e.stopPropagation()}
            style={{ minWidth: "280px" }}
          >
            <label className="mb-1">Estado:</label>
            <select
              className="form-select mt-1"
              id="status"
              name="status"
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              value={filters.status}
            >
              <option value="all">Todos</option>
              <option value="pending">Pendientes</option>
              <option value="approved">Aprobados</option>
              <option value="rejected">Rechazados</option>
            </select>

            <label className="mt-3 mb-1">Sucursal:</label>
            <select
              className="form-select mt-1"
              id="branchId"
              name="branchId"
              onChange={(e) => setFilters({ ...filters, branchId: e.target.value })}
              value={filters.branchId}
              disabled={loadingBranches}
            >
              <option value="all">Todas las sucursales</option>
              {branches?.map((branch: any) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>

            <div className="row mt-3 g-2">
              <div className="col-6">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFilters(initialValues);
                    dispatch(setQuery(""));
                  }}
                  className="btn btn-amarillo d-block w-100 btn-lg fs-14px"
                >
                  Limpiar
                </button>
              </div>
              <div className="col-6">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    updateFilters();
                  }}
                  className="btn btn-azul d-block w-100 btn-lg fs-14px"
                >
                  Aplicar
                </button>
              </div>
            </div>
          </div>

          <div className="flex-fill position-relative">
            <div className="input-group">
              <div
                className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
                style={{ zIndex: 10 }}
              >
                {timer ? (
                  <i className="fa fa-spinner fa-spin" />
                ) : (
                  <i className="fa fa-search opacity-5" />
                )}
              </div>
              <input
                type="text"
                className="form-control px-35px bg-light"
                placeholder="Buscar por nombre, apellido o descripción..."
                id="name"
                name="name"
                onChange={handleSearchChange}
                value={filters.param}
                style={{ borderRadius: "0 999px 999px 0" }}
              />
            </div>
          </div>
        </div>
        {children && <div style={{ flexShrink: 0 }}>{children}</div>}
      </div>
      <div className="mb-3">
        {selected.length > 0 &&
          selected.map((filter) => (
            <span
              key={filter.key}
              className="badge bg-primary text-white fs-6 me-2 position-relative pe-4"
            >
              {filter.keyName}: {filter.valueName}
              <button
                type="button"
                className="btn-close btn-close-white position-absolute top-0 end-0 mt-1 me-1 ps-1"
                aria-label="Cerrar"
                onClick={() => removeFilter(filter.key)}
              />
            </span>
          ))}
      </div>
    </div>
  );
};

export default FilterHolidays;
